"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// Aanpasbare marketing-pop-up (nieuwsbrief / actie / kortingscode / mededeling).
// Toont na de ingestelde vertraging én nadat de bezoeker iets gedaan heeft (scrollen, tikken,
// klikken, toets), alleen op de pagina waar de bezoeker binnenkwam, en maximaal één keer per
// sessie. Instelbaar via /admin/instellingen.
const SESSION_KEY = "mow_popup_seen";

// MOW-PERF-OKT26 (sitecontrole 18-10, PERF-LCP): de pop-upfoto was op winkel en categorieën het
// grootste element van de pagina en verscheen na 5 s, ook voor wie nog niets had gedaan: LCP
// 9-15 s. Chrome stopt de LCP-meting bij de eerste interactie van een bezoeker, dus een pop-up
// die pas daarna komt telt niet mee (de labmeting doet geen interactie). Zelfde gebeurtenissen
// als lib/use-load-after-intent.ts: bewust géén "scroll"/"focusin" (die vuurt de browser zelf af).
const INTERACTIE_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
// Na de eerste interactie nog even wachten: een pop-up die midden in een klik opent, vangt die klik op.
const NA_INTERACTIE_MS = 1500;
// Hoe lang we op het inladen van de foto wachten voordat de pop-up zonder foto-wachttijd opent.
const FOTO_MAX_WACHT_MS = 3000;

export default function MarketingPopup() {
  const s = useSiteSettings();
  const [open, setOpen] = useState(false);
  // De pagina waarop de bezoeker binnenkwam (deze component mount één keer).
  const entryPath = useRef<string | null>(null);
  const pathname = usePathname();
  if (entryPath.current === null) entryPath.current = pathname;

  useEffect(() => {
    if (!s.popupEnabled) return;
    if (typeof window === "undefined") return;
    const now = new Date();
    if (s.popupStartDate && now < new Date(s.popupStartDate)) return;
    if (s.popupEndDate) {
      const end = new Date(s.popupEndDate);
      end.setHours(23, 59, 59, 999); // t/m einddag
      if (now > end) return;
    }
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return; // al getoond deze sessie
    } catch { /* sessionStorage geblokkeerd → gewoon tonen */ }

    // >>> MOW-PERF-OKT26
    // Foto ophalen en decoderen vóór de pop-up opent, zodat hij er in één keer compleet staat
    // (geen leeg vlak dat daarna uitzet). Pas vanaf de eerste interactie: wie niets doet krijgt
    // de pop-up niet en hoeft de foto dus niet te laden (hij concurreerde met de bannerfoto).
    let beeld: Promise<unknown> | null = null;
    const laadBeeld = () => {
      if (beeld || !s.popupImage) return;
      const voorbeeld = new Image();
      voorbeeld.decoding = "async";
      voorbeeld.src = s.popupImage;
      beeld = voorbeeld.decode().catch(() => { /* niet te decoderen: de pop-up laadt hem dan zelf */ });
    };

    const delay = Math.max(0, Number(s.popupDelaySeconds) || 0) * 1000;
    let vertragingVoorbij = false;
    let interactieKlaar = false;
    let timers: number[] = [];

    const opruimen = () => {
      for (const naam of INTERACTIE_EVENTS) window.removeEventListener(naam, opInteractie);
      timers.forEach((t) => window.clearTimeout(t));
      timers = [];
    };
    const toon = () => {
      opruimen();
      // Alleen tonen als de bezoeker nog op de landingspagina is.
      if (window.location.pathname !== entryPath.current) return;
      const wacht = new Promise((resolve) => window.setTimeout(resolve, FOTO_MAX_WACHT_MS));
      Promise.race([beeld ?? Promise.resolve(), wacht]).then(() => {
        if (window.location.pathname !== entryPath.current) return;
        setOpen(true);
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
      });
    };
    const controleer = () => { if (vertragingVoorbij && interactieKlaar) toon(); };
    function opInteractie() {
      for (const naam of INTERACTIE_EVENTS) window.removeEventListener(naam, opInteractie);
      laadBeeld();
      timers.push(window.setTimeout(() => { interactieKlaar = true; controleer(); }, NA_INTERACTIE_MS));
    }

    for (const naam of INTERACTIE_EVENTS) window.addEventListener(naam, opInteractie, { passive: true });
    timers.push(window.setTimeout(() => { vertragingVoorbij = true; controleer(); }, delay));
    return opruimen;
  }, [s.popupEnabled, s.popupDelaySeconds, s.popupStartDate, s.popupEndDate, s.popupImage]); // MOW-PERF-OKT26: popupImage erbij (voorbeeldfoto)

  if (!open) return null;

  return (
    <div className="mp-overlay" role="dialog" aria-modal="true" aria-label={s.popupTitle || "Mededeling"} onClick={() => setOpen(false)}>
      <div className={s.popupImage ? "mp-panel mp-panel--with-image" : "mp-panel"} onClick={(e) => e.stopPropagation()}>
        <button className="mp-close" onClick={() => setOpen(false)} aria-label="Sluiten">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {s.popupImage && <img className="mp-image" src={s.popupImage} alt="" />}
        <div className="mp-body">
          {s.popupTitle && <h3 className="mp-title">{s.popupTitle}</h3>}
          {s.popupText && <p className="mp-text">{s.popupText}</p>}
          {s.popupButtonLabel && s.popupButtonLink && (
            <Link href={s.popupButtonLink} className="btn btn-primary" onClick={() => setOpen(false)} style={{ marginTop: "12px" }}>
              {s.popupButtonLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
